/* eslint-disable react-hooks/exhaustive-deps */
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import { Page, Text, View, Document, StyleSheet, pdf, Font, Image } from "@react-pdf/renderer";
import { useTranslation } from 'react-i18next';
import Rudaw from '../../../assets/fonts/rudawregular2.ttf';
import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { formatPrice } from '../../../utils';
import ReportService from '../../../services/reportService';
import { useCompany } from '../../../provider/companyProvider';
import { fileApiRoute } from "../../../services/networkService";

const registerFont = () => {
    Font.register({
        family: 'Rudaw',
        fonts: [{ src: Rudaw, fontWeight: 'normal' }],
    });
};

const PdfTotalActivityReport = ({ data }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language !== 'en';
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { business } = useCompany();

    const styles = StyleSheet.create({
        page: {
            padding: 30,
            direction: isRTL ? 'rtl' : 'ltr',
            fontFamily: 'Rudaw',
        },
        title: {
            fontSize: 18,
            textAlign: "center",
            fontWeight: "bold",
            marginTop: 10
        },
        infoSection: {
            marginBottom: 10,
            padding: 8,
            border: "1px solid #ccc",
            borderRadius: 5,
            fontSize: 14,
        },
        row: {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            paddingVertical: 3,
            alignItems: 'center',
            direction: isRTL ? 'rtl' : 'ltr',
        },
        key: {
            fontWeight: 'bold',
            width: '40%',
            textAlign: isRTL ? 'right' : 'left',
            direction: isRTL ? 'rtl' : 'ltr',
        },
        value: {
            flex: 1,
            textAlign: isRTL ? 'right' : 'left',
            paddingLeft: 1,
        },
        table: {
            display: "table",
            width: "auto",
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "#ccc",
            marginBottom: 20,
        },
        tableRow: {
            flexDirection: isRTL ? "row-reverse" : "row",
        },
        tableCell: {
            padding: 5,
            borderRightWidth: 1,
            borderBottomWidth: 1,
            borderColor: "#ccc",
            textAlign: isRTL ? 'right' : "left",
            fontSize: 10,
        },
        footer: {
            display: 'flex',
            flexDirection: isRTL ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
            paddingVertical: 3,
            alignItems: 'center',
            direction: isRTL ? 'rtl' : 'ltr',
            textAlign: isRTL ? 'right' : 'left',
            fontSize: 12
        },
    });

    const MyDocument = () => {
        const { branch } = data;

        return (
            <Document>
                <Page size="A4" style={styles.page}>
                    {/* Title */}
                    <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", marginBottom: 4 }}>
                        <Image src={`${fileApiRoute}/${business.logo}`} style={{ width: 100, height: 50 }} />
                        <View style={{ width: 10 }}></View>
                        <Text style={styles.title}>{t('totalBalanceSheet')}</Text>
                    </View>

                    {/* Information Section */}
                    <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                        <View style={[styles.infoSection, { flex: 1, marginRight: 10 }]}>
                            <View style={styles.row}>
                                <Text style={styles.key}>{t('branch')}:</Text>
                                <Text style={styles.value}>{branch ? branch.name : t('allBranches')}</Text>
                            </View>
                        </View>
                    </View>


                    {/* Table */}
                    <View style={styles.table}>
                        {/* Table Header */}
                        <View style={[styles.tableRow, { backgroundColor: "#f0f0f0" }]}>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('cashBalance')}</Text>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('liabilities')}</Text>
                        </View>

                        {/* Table Rows */}
                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { width: '50%' }]}>
                                {t('walletMoney')} : {formatPrice(report.totalBranchWallet)}
                                {'\n'}
                                {'\n'}
                                {t('driverWallet')} : {formatPrice(report.totalDriverWallet)}
                            </Text>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('pageWallet')} : {formatPrice(report.totalPageWallet)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('total')} : {formatPrice(report.totalWealth)}</Text>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('total')} : {formatPrice(report.totalPageWallet)}</Text>
                        </View>

                        <View style={styles.tableRow}>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{t('totalWealth')}:</Text>
                            <Text style={[styles.tableCell, { width: '50%' }]}>{formatPrice(report.netWealth)}</Text>
                        </View>

                    </View>

                </Page>
            </Document>
        );
    };


    const fetchReport = async () => {
        try {
            setIsLoading(true);
            const filters = data && {
                branchId: data.branch && data.branch.id,
            };

            const responseData = await ReportService.fetchActivity(filters);
            setReport(responseData);

        } catch (error) {
            console.error("Failed to fetch report:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrint = async () => {
        registerFont();
        await fetchReport();
    };

    useEffect(() => {
        if (report) {
            (async () => {
                try {
                    const blob = await pdf(<MyDocument />).toBlob();
                    if (blob) {
                        const url = URL.createObjectURL(blob);
                        window.open(url);
                    } else {
                        console.error("Failed to generate PDF blob.");
                    }
                } catch (error) {
                    console.error("Error generating PDF:", error);
                }
            })();
        }
    }, [report]);

    return (
        <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', width: 140, height: 40 }}
            onClick={handlePrint}
        >
            {isLoading ? <CircularProgress color="secondary" size={28} /> :
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p>{t('print')}</p>
                    <Box width={'6px'}> </Box>
                    <LocalPrintshopOutlinedIcon fontSize="small" />
                </Box>
            }
        </Button>
    );
}

export default PdfTotalActivityReport;